import React from "react";
import { ArrowRight, CheckCircle2, Crown, Sparkles, Users } from "lucide-react";
import { Link } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import { SurfaceCard } from "../components/designSystem";

const plans = [
  {
    name: "Free",
    price: "₦0",
    description: "For traders starting to build journaling discipline and a consistent trade log.",
    cta: "/signup",
    ctaLabel: "Get Started",
    icon: Sparkles,
    featured: false,
    features: ["Core journaling", "Basic analytics", "Trade history", "30 trades per month"],
  },
  {
    name: "Pro",
    price: "₦10k/mo",
    description: "For serious individual traders who want deeper analytics, exports, and structured review tools.",
    cta: "/upgrade",
    ctaLabel: "Upgrade to Pro",
    icon: CheckCircle2,
    featured: true,
    features: ["Advanced analytics", "Unlimited trade reviews", "Journal and messaging", "CSV exports"],
  },
  {
    name: "Enterprise",
    price: "Custom",
    description: "For trading teams, mentors, and communities that need shared workspace visibility and coordination.",
    cta: "/contact",
    ctaLabel: "Talk to Us",
    icon: Crown,
    featured: false,
    features: ["Team workspace", "Shared communication", "Operational visibility", "Priority support"],
  },
];

function Pricing() {
  return (
    <div className="min-h-screen bg-[#f3efe7] text-slate-950 dark:bg-[linear-gradient(180deg,_#020617_0%,_#111827_45%,_#0f172a_100%)] dark:text-slate-100">
      <PageHeader 
        title="Pricing" 
        subtitle="Clear pricing for traders who want more structure, better review depth, and room to grow into team workflows."
        eyebrow="Plans"
      />
      <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10">
        <div className="mb-10 grid gap-5 md:grid-cols-3">
          <SurfaceCard className="bg-white dark:bg-gray-900">
            <div className="flex items-center gap-3">
              <Sparkles className="h-5 w-5 text-sky-600 dark:text-sky-300" />
              <p className="text-sm font-semibold text-slate-950 dark:text-white">Start cleanly</p>
            </div>
            <p className="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">Free covers the basics so traders can start documenting results without friction.</p>
          </SurfaceCard>
          <SurfaceCard className="bg-white dark:bg-gray-900">
            <div className="flex items-center gap-3">
              <ArrowRight className="h-5 w-5 text-amber-600 dark:text-amber-300" />
              <p className="text-sm font-semibold text-slate-950 dark:text-white">Grow into review depth</p>
            </div>
            <p className="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">Pro is where the journal starts behaving like a serious performance tool.</p>
          </SurfaceCard>
          <SurfaceCard className="bg-white dark:bg-gray-900">
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 text-fuchsia-600 dark:text-fuchsia-300" />
              <p className="text-sm font-semibold text-slate-950 dark:text-white">Scale to teams</p>
            </div>
            <p className="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">Enterprise is for coordinated trading environments, not just solo journaling.</p>
          </SurfaceCard>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {plans.map((plan) => {
            const Icon = plan.icon;

            return (
              <SurfaceCard
                key={plan.name}
                className={plan.featured ? "border-sky-300 bg-[linear-gradient(180deg,_#ffffff_0%,_#f2f8ff_100%)] shadow-[0_30px_80px_-50px_rgba(14,165,233,0.7)] dark:border-sky-500/30 dark:bg-[linear-gradient(180deg,_#111827_0%,_#0f172a_100%)]" : "bg-white dark:bg-gray-900"}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="rounded-2xl bg-[#f3efe7] p-3 text-slate-900 dark:bg-gray-800 dark:text-white">
                    <Icon className="h-5 w-5" />
                  </div>
                  {plan.featured ? <span className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-300">Best current value</span> : null}
                </div>
                <h2 className="mt-6 text-2xl font-semibold text-slate-950 dark:text-white">{plan.name}</h2>
                <p className="mt-2 text-4xl font-semibold tracking-tight text-slate-950 dark:text-white">{plan.price}</p>
                <p className="mt-4 text-sm leading-7 text-slate-600 dark:text-slate-300">{plan.description}</p>
                <div className="mt-6 space-y-3">
                  {plan.features.map((feature) => (
                    <div key={feature} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 dark:border-gray-700 dark:bg-gray-800 dark:text-slate-200">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-300" />
                      {feature}
                    </div>
                  ))}
                </div>
                <Link
                  to={plan.cta}
                  className={`mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition ${plan.featured ? "bg-sky-600 text-white hover:bg-sky-700" : "bg-slate-950 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200"}`}
                >
                  {plan.ctaLabel}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </SurfaceCard>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default Pricing;